/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { WebscriptApi } from '@alfresco/js-api';
import { Observable, from } from 'rxjs';

export interface AdminPerson {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  enabled?: boolean;
}

export interface AdminGroup {
  id: string;
  displayName?: string;
  isRoot?: boolean;
}

export interface AuditApp {
  id: string;
  name?: string;
  isEnabled?: boolean;
}

export interface AuditEntry {
  id: number;
  auditApplicationId?: string;
  createdByUser?: { id?: string; displayName?: string };
  createdAt?: string;
  values?: any;
}

/** Thin wrapper over the Alfresco core REST APIs used by the Admin Tools pages. */
@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly api = inject(AlfrescoApiService);

  /** Core public API (contentClient.basePath = `{ecm}/.../public/alfresco/versions/1`). */
  private call(path: string, method: string, body?: any, query?: any): Observable<any> {
    const inst: any = this.api.getInstance();
    return from(
      inst.contentClient.callApi(path, method, {}, query ?? {}, {}, {}, body ?? null, ['application/json'], ['application/json']) as Promise<any>
    );
  }

  /** Legacy webscript (`{ecm}/service/...`) — used for hard-deleting a user, which the public API can't do. */
  private legacy(method: string, scriptPath: string): Observable<any> {
    const ws = new WebscriptApi(this.api.getInstance() as any);
    return from(ws.executeWebScript(method, scriptPath) as Promise<any>);
  }

  // ---- People ----
  listPeople(skipCount = 0, maxItems = 100): Observable<any> {
    return this.call('/people', 'GET', null, { skipCount, maxItems, orderBy: 'id ASC' });
  }

  getPerson(id: string): Observable<any> {
    return this.call(`/people/${encodeURIComponent(id)}`, 'GET');
  }

  createPerson(body: any): Observable<any> {
    return this.call('/people', 'POST', body);
  }

  updatePerson(id: string, body: any): Observable<any> {
    return this.call(`/people/${encodeURIComponent(id)}`, 'PUT', body);
  }

  /** Hard-delete via the legacy webscript (`DELETE /alfresco/service/api/people/{id}`). */
  deletePerson(id: string): Observable<any> {
    return this.legacy('DELETE', `api/people/${encodeURIComponent(id)}`);
  }

  // ---- Groups ----
  /**
   * Groups, with their authority `zones`.
   *
   * The zone is the only server-side signal that distinguishes a Share site's
   * groups (`APP.SHARE`) from directory groups, so it is always requested.
   */
  listGroups(skipCount = 0, maxItems = 100): Observable<any> {
    return this.call('/groups', 'GET', null, { skipCount, maxItems, orderBy: 'displayName ASC', include: 'zones' });
  }

  createGroup(body: any): Observable<any> {
    return this.call('/groups', 'POST', body);
  }

  updateGroup(id: string, body: any): Observable<any> {
    return this.call(`/groups/${encodeURIComponent(id)}`, 'PUT', body);
  }

  deleteGroup(id: string): Observable<any> {
    return this.call(`/groups/${encodeURIComponent(id)}`, 'DELETE');
  }

  // ---- Group membership ----
  /** Groups a person belongs to. */
  listPersonGroups(personId: string, maxItems = 200): Observable<any> {
    return this.call(`/people/${encodeURIComponent(personId)}/groups`, 'GET', null, { maxItems, orderBy: 'displayName ASC' });
  }

  addGroupMember(groupId: string, personId: string): Observable<any> {
    return this.call(`/groups/${encodeURIComponent(groupId)}/members`, 'POST', { id: personId, memberType: 'PERSON' });
  }

  removeGroupMember(groupId: string, personId: string): Observable<any> {
    return this.call(`/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(personId)}`, 'DELETE');
  }

  /**
   * Direct members (users + sub-groups) of a group, via the legacy webscript.
   * The public `/groups/{id}/members` API relies on the search index, which is
   * unreliable on Community, so we use `/api/groups/{shortName}/children`.
   */
  listGroupChildren(groupId: string): Observable<any> {
    const shortName = groupId.replace(/^GROUP_/, '');
    return this.legacy('GET', `api/groups/${encodeURIComponent(shortName)}/children`);
  }

  /** Adds a user (PERSON) or sub-group (GROUP) to a group. */
  addMember(groupId: string, memberId: string, memberType: 'PERSON' | 'GROUP'): Observable<any> {
    return this.call(`/groups/${encodeURIComponent(groupId)}/members`, 'POST', { id: memberId, memberType });
  }

  removeMember(groupId: string, memberId: string): Observable<any> {
    return this.call(`/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(memberId)}`, 'DELETE');
  }

  // ---- Audit ----
  listAuditApps(): Observable<any> {
    return this.call('/audit-applications', 'GET');
  }

  /** One page of an application's entries, newest first; `where` narrows it on the server. */
  listAuditEntries(appId: string, skipCount = 0, maxItems = 50, where?: string): Observable<any> {
    return this.call(`/audit-applications/${encodeURIComponent(appId)}/audit-entries`, 'GET', null, {
      skipCount,
      maxItems,
      orderBy: 'createdAt DESC',
      include: 'values',
      ...(where ? { where } : {})
    });
  }
}
